import {yupResolver} from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {BoxParentHeight, Select, Table} from '@ozark/common/components';
import firebase from 'firebase/compat/app';
import {useState} from 'react';
import {useForm} from 'react-hook-form';
import * as yup from 'yup';
import {StoragePath} from '../../..';
import {Firebase} from '../../../firebase';
import {useGroup, useUserInfo} from '../../../hooks';
import {formatYearMonth} from '../EquipmentExpenseResiduals/helpers';

export const Schema = yup.object().shape({
  columns: yup.object({
    agentName: yup.string().required(),
    agentCode: yup.string().required(),
    description: yup.string().required(),
    amount: yup.string().required(),
    revShare: yup.string().required(),
  }),
});

interface Props {
  onSubmit: (data: {columns: Record<string, string>[]; yearMonth: string; fileUrl: string}) => void;
  onCancel: () => void;
  csvObjects: any[];
  csvBuffer: ArrayBuffer;
  defaultValues: any;
  yearMonths: string[] | null;
  isLoading: boolean;
}

export const Import = ({
  onSubmit,
  onCancel,
  csvObjects,
  csvBuffer,
  defaultValues,
  yearMonths,
  isLoading,
}: Props) => {
  const hookForm = useForm({
    resolver: yupResolver(Schema),
    defaultValues: {...defaultValues},
    shouldUnregister: true,
  });
  const {isErpUser, userGroupId} = useUserInfo();
  const {document: group} = useGroup(userGroupId);

  const {
    handleSubmit,
    formState: {errors},
    control,
  } = hookForm;

  const [loading, setLoading] = useState(false);
  const [selectedYearMonth, setSelectedYearMonth] = useState<string>();

  const upload = (path: string, name: string, arrayBuffer: ArrayBuffer) => {
    return new Promise((resolve, reject) => {
      const task = Firebase.storage.ref(`${path}/${name}`).put(arrayBuffer);
      task.on(
        firebase.storage.TaskEvent.STATE_CHANGED,
        (snapshot: any) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          console.log(`uploading: ${progress}`);
        },
        (err: any) => {
          console.error(err);
          reject(err);
        },
        () => {
          Firebase.storage
            .ref(path)
            .child(name)
            .getDownloadURL()
            .then(url => {
              resolve(url);
            });
        }
      );
    });
  };

  const onSuccess = async (data: any) => {
    const mapping = data.columns;

    let result = csvObjects.map((row: any) => {
      const newRow: any = {};
      Object.keys(mapping).forEach(key => {
        newRow[key] = row[mapping[key]];
      });
      return newRow;
    });

    if (!isErpUser) {
      result = result.map((row: any) => {
        return {
          ...row,
          group: group?.data?.name,
        };
      });
    }

    if (!selectedYearMonth) return;

    setLoading(true);

    const yearMonthString = selectedYearMonth;
    const fileUrl = await upload(
      StoragePath.residualsManualAdjustments,
      `${yearMonthString}-${Date.now()}.csv`,
      csvBuffer
    );

    onSubmit({
      columns: result,
      yearMonth: yearMonthString,
      fileUrl: fileUrl as string,
    });

    setLoading(false);
  };

  const onError = (_data: any) => {};

  const columnKeyItems =
    csvObjects &&
    Object.keys(csvObjects[0])
      .sort()
      .map(key => (
        <MenuItem key={key} value={key}>
          {key}
        </MenuItem>
      ));

  const generateColumnSelect = (name: string, label: string) => {
    return (
      <Select name={`columns.${name}`} label={label} required errors={errors} control={control}>
        {columnKeyItems}
      </Select>
    );
  };

  return (
    <BoxParentHeight component={Paper} display="flex" flexDirection="column" p={2} overflow="unset">
      <Grid
        container
        spacing={4}
        direction="row"
        alignItems="stretch"
        height="100%"
        overflow="auto"
      >
        <Grid item xs={4} height="100%">
          <Box
            overflow="auto"
            height="100%"
            pb={0}
            mb={0}
            borderBottom="1px solid rgba(224, 224, 224, 1)"
          >
            <Box>
              <Box sx={{mb: 2}}>
                <Typography
                  sx={{fontSize: {xs: '1em', md: '1.2em'}}}
                  variant="caption"
                  gutterBottom
                >
                  File Month &amp; Year
                </Typography>
                <Divider />
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={12} pr={1}>
                  <TextField
                    value={selectedYearMonth}
                    onChange={e => setSelectedYearMonth(e.target.value)}
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                    }}
                    sx={{
                      backgroundColor: 'transparent !important',
                      minWidth: '100%',
                      border: '1px solid #c4c4c4',
                      borderRadius: '4px',
                      padding: '10px',
                      '&:hover': {
                        border: '1px solid #000000',
                      },
                    }}
                    select
                  >
                    {yearMonths?.map(e => {
                      return (
                        <MenuItem key={e} value={e}>
                          {formatYearMonth(e)}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>
              </Grid>
              <Box sx={{mb: 2, mt: 2}}>
                <Typography
                  sx={{fontSize: {xs: '1em', md: '1.2em'}}}
                  variant="caption"
                  gutterBottom
                >
                  Column Mapping
                </Typography>
                <Divider />
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box pr={1}>
                    {generateColumnSelect('agentName', 'Agent Name')}
                    {generateColumnSelect('agentCode', 'Agent Code')}
                    {isErpUser && generateColumnSelect('group', 'Group Name')}
                    {generateColumnSelect('description', 'Description')}
                    {generateColumnSelect('amount', 'Amount')}
                    {generateColumnSelect('revShare', 'Rev Share')}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={8} height="100%">
          <Box display="flex" flexDirection="column" height="100%">
            <Box sx={{mb: 2}}>
              <Typography sx={{fontSize: {xs: '1em', md: '1.2em'}}} variant="caption" gutterBottom>
                File Content Preview
              </Typography>
              <Divider />
            </Box>
            <Box
              maxWidth="600px"
              minWidth="100%"
              overflow="auto"
              flexGrow="1"
              sx={{'& > div': {height: '100%'}}}
            >
              <Table
                rows={csvObjects.slice(0, 10).map((e, i) => ({...e, id: i}))}
                columns={Object.keys(csvObjects[0]).map((e: string) => ({
                  id: e,
                  label: e,
                }))}
                noWrap
                flat
                scrollableBody
                stickyHeader
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Divider sx={{my: 2}} />
      <Box sx={{display: 'flex', justifyContent: 'flex-end', '& > *': {ml: 4}}}>
        <Button onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSuccess, onError)}
          color="primary"
          variant="contained"
          disabled={
            isLoading || !selectedYearMonth || !csvObjects.length || (!isErpUser && group.promised)
          }
        >
          {isLoading && (
            <CircularProgress
              sx={{position: 'absolute', top: '50%', left: '50%', mt: -1.5, ml: -1.5}}
              size={24}
            />
          )}
          {!isLoading && 'Save'}
        </Button>
      </Box>
    </BoxParentHeight>
  );
};
